import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './locales/en';
import ru from './locales/ru';

const CATALOGS = { en, ru };
const SUPPORTED = ['ru', 'en'];
const DEFAULT_LOCALE = 'ru';
const STORAGE_KEY = 'cmr_locale';

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key,
});

function lookup(catalog, key) {
  if (catalog[key] !== undefined) return catalog[key];
  const parts = key.split('.');
  let node = catalog;
  for (const p of parts) {
    if (node == null || typeof node !== 'object') return undefined;
    node = node[p];
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, name) =>
    vars[name] !== undefined && vars[name] !== null ? String(vars[name]) : `{${name}}`,
  );
}

// Russian plural rules: 1 / 2-4 / 5+ / 0
// Pass {count, one, few, many} — falls back gracefully.
function pluralRu(count, forms) {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return forms.many ?? forms.few ?? forms.one;
  if (n1 > 1 && n1 < 5) return forms.few ?? forms.one;
  if (n1 === 1) return forms.one;
  return forms.many ?? forms.few ?? forms.one;
}

function pluralEn(count, forms) {
  return Math.abs(count) === 1 ? forms.one : (forms.many ?? forms.few ?? forms.one);
}

function readStored() {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return SUPPORTED.includes(v) ? v : DEFAULT_LOCALE;
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(readStored);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    window.localStorage.setItem(STORAGE_KEY, next);
    setLocaleState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const catalog = CATALOGS[locale] || CATALOGS[DEFAULT_LOCALE];
      const fallback = CATALOGS[DEFAULT_LOCALE];
      let raw = lookup(catalog, key);
      if (raw === undefined) raw = lookup(fallback, key);
      if (raw === undefined) return key;
      if (vars && typeof vars.count === 'number' && typeof raw === 'object') {
        const plural = locale === 'ru' ? pluralRu(vars.count, raw) : pluralEn(vars.count, raw);
        return interpolate(plural ?? '', vars);
      }
      if (typeof raw !== 'string') return key;
      return interpolate(raw, vars);
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useContext(I18nContext).t;
}

export const SUPPORTED_LOCALES = SUPPORTED;
